import { assetNameComponentsFromName } from './common';
import { Config } from './video-data/config';
import { ALL_ASPECT_RATIOS } from './aspectRatioConfig';
import type { TemplateWatermarkSource } from './core/template/types';
import { TemplateWatermarkProperties } from './core/template/types';
import type { MediaSourceData } from './core/asset/MediaAsset';
import type { TemplateBundle } from './types';
import type { AudioSourceData } from './audio/AudioAsset';
import { type IconSourceData } from './core/asset/IconAsset';
import type { TranscriptData } from './video-data/types';
import { ErrorReporter } from './libs/error-reporter';

export {
    assertIsValidLottie,
    assertIsValidLottieDuration,
    assertIsValidTransitionLottie,
} from './libs/Lottie/validators';

class ValidationError extends Error {
    override name = 'ValidationError';
}

/**
 * These methods are utility methods to help with common validation logic that setters typically have while ensuring
 * that incoming values are valid.
 *
 * Invalid values raise an Error.
 *
 * For example:
 *  set aspectRatio(value) {
 *      assertString(value);
 *      assertInEnum(value, VideoData.ALL_ASPECT_RATIOS);
 *      // now that we know it's a valid value, do something with it!
 *  }
 */
export function assertBoolean(value: boolean, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (typeof value !== 'boolean') {
        ErrorReporter.setContext('assertBoolean', { value, nullable });
        throw new ValidationError('expected value to be a boolean');
    }
}

export function assertString(value: string, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (typeof value !== 'string') {
        ErrorReporter.setContext('assertString', { value, nullable });
        throw new ValidationError('expected value to be a string');
    }
}

export function assertNumber(value: number, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (typeof value !== 'number') {
        ErrorReporter.setContext('assertNumber', { value, nullable });
        throw new ValidationError('expected value to be a number');
    }
    if (isNaN(value)) {
        ErrorReporter.setContext('assertNumber', { value, nullable });
        throw new ValidationError('expected value to be a number');
    }
}

export function assertStringOrNumber(value: number | string, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (typeof value !== 'number' && typeof value !== 'string') {
        ErrorReporter.setContext('assertStringOrNumber', { value, nullable });
        throw new ValidationError('expected value to be a string or number');
    }
}

export function assertUuid(value: string) {
    // validate that value is a valid uuid v4 value
    // https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)
    assertString(value);
    if (value.split('-').length !== 5) {
        ErrorReporter.setContext('assertUuid', { value });
        throw new ValidationError('invalid uuid: expected value to have 5 parts');
    }
    if (value.length !== 36) {
        ErrorReporter.setContext('assertUuid', { value });
        throw new ValidationError('invalid uuid: expected value to be 36 characters');
    }
}

export function assertObject(value: object, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (typeof value !== 'object') {
        ErrorReporter.setContext('assertObject', { value, nullable });
        throw new ValidationError('expected value to be an object');
    }
}

export function assertArray(value: unknown[], nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (!Array.isArray(value)) {
        ErrorReporter.setContext('assertArray', { value, nullable });
        throw new ValidationError('expected value to be an array');
    }
}

export function assertNumberBetween(
    value: number,
    minValue: number,
    maxValue: number,
    nullable = false,
) {
    if (nullable && value === null) {
        return;
    }
    assertNumber(value);
    assertMinimum(value, minValue);
    assertMaximum(value, maxValue);
}

export function assertMinimum(value: number, minValue: number, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (value < minValue) {
        ErrorReporter.setContext('assertMinimum', { value, minValue, nullable });
        throw new ValidationError('expected value to not be less than minimum value');
    }
}

export function assertGreaterThan(value: number, minValue: number, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    if (value <= minValue) {
        ErrorReporter.setContext('assertGreaterThan', { value, minValue, nullable });
        throw new ValidationError('expected value to be greater than minimum value');
    }
}

export function assertMaximum(value: number, maxValue: number) {
    if (value > maxValue) {
        ErrorReporter.setContext('assertMaximum', { value, maxValue });
        throw new ValidationError('expected value to not exceed maximum value');
    }
}

export function assertMinimumLength<T>(value: T[], length: number) {
    if (value.length < length) {
        ErrorReporter.setContext('assertMinimumLength', { value, length });
        throw new ValidationError('expected value to have a minimum length');
    }
}

export function assertKeyInObject(key: string, object: object) {
    if (!(key in object)) {
        ErrorReporter.setContext('assertKeyInObject', { key, object });
        throw new ValidationError('expected key to be in object');
    }
}

export function assertInArray<T>(value: T, array: readonly T[]) {
    if (!array.includes(value)) {
        ErrorReporter.setContext('assertInArray', { value, array });
        throw new ValidationError('expected value to be in array');
    }
}

export function assertNotInArray<T>(value: T, array: T[]) {
    if (array.includes(value)) {
        ErrorReporter.setContext('assertNotInArray', { value, array });
        throw new ValidationError('expected value to not be in array');
    }
}

export function assertInEnum(value: string | number, enumObj: object) {
    assertObject(enumObj);
    const values = Object.values(enumObj);
    if (!values.includes(value)) {
        ErrorReporter.setContext('assertInEnum', { value, enumObj });
        throw new ValidationError('expected value to be enumerated in Enum');
    }
}

export function assertValuesInArray<T>(values: T[], array: T[] | readonly T[]) {
    values.forEach((value) => assertInArray(value, array));
}

export function assertIsValidHexColor(value: number, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    assertNumber(value);
    assertMinimum(value, 0x000000);
    assertMaximum(value, 0xffffff);
}

export function assertIsValidAlpha(value: number, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    assertNumber(value);
    assertMinimum(value, 0.0);
    assertMaximum(value, 1.0);
}

export function isUrl(value: string) {
    return value.indexOf('/') !== -1;
}

export function assertIsValidUrl(value: string, nullable = false) {
    if (nullable && value === null) {
        return;
    }
    assertString(value);
    // NOTE: must support both HTTP/HTTPS urls and relative URLs (for testing w/ local assets)
    if (!isUrl(value)) {
        ErrorReporter.setContext('assertIsValidUrl', { value, nullable });
        throw new ValidationError('expected value to be valid URL');
    }
}

export function assertHasOwnProperties(obj: object, props: string[]) {
    props.forEach((prop) => {
        if (!obj.hasOwnProperty(prop)) {
            ErrorReporter.setContext('assertHasOwnProperties', { obj, prop });
            throw new ValidationError('expected object to have property"');
        }
    });
}

export function assertIsValidTemplateBundle(value: TemplateBundle) {
    assertObject(value);
    assertHasOwnProperties(value, ['sceneDesigns', 'templateData']);
    for (const sceneDesignsId in value.sceneDesigns) {
        const rawSceneDesign = value.sceneDesigns[sceneDesignsId];

        const existingAspectRatios = ALL_ASPECT_RATIOS.map(
            (aspectRatio) => rawSceneDesign[aspectRatio],
        ).filter((f) => f !== undefined);

        if (existingAspectRatios.length < 1) {
            ErrorReporter.setContext('assertHasAtLeastOne', { value });
            throw new ValidationError('expected list to have at least one item');
        }

        existingAspectRatios.forEach((sceneDesign) => {
            assertObject(sceneDesign as object);
        });
    }
}

/**
 * Asserts the expected audio structure
 */
export function assertIsValidAudio(value: AudioSourceData, nullable = false) {
    if (nullable && value === null) {
        return;
    }

    assertObject(value);

    // check required fields
    assertHasOwnProperties(value, ['mp3_url', 'duration', 'source', 'source_id']);
    assertIsValidUrl(value.mp3_url);
    assertNumber(value.duration, true);
    assertNumber(value.source);
    assertString(value.source_id as string, true);

    // check optional fields
    value.hasOwnProperty('license') && assertInArray(value.license, Config.LICENSES);
    value.hasOwnProperty('attribution') && assertString(value.attribution as string, true);
    value.hasOwnProperty('description') && assertString(value.description as string, true);
    value.hasOwnProperty('tags') && assertArray(value.tags);
    value.hasOwnProperty('title') && assertString(value.title as string, true);
    value.hasOwnProperty('name') && assertString(value.name as string, true);
    value.hasOwnProperty('quality') && assertNumber(value.quality);
}

/**
 * Validates everything that is both in watermark source data and media source data
 */
function assertIsValidBaseSourceData(sourceData: MediaSourceData) {
    assertObject(sourceData);
    if (sourceData && Object.keys(sourceData).length === 0) {
        // passed in '{}' which is unset, don't do anything
        return;
    }
    // verify static image and video common properties
    assertHasOwnProperties(sourceData, ['thumbnail_url', 'preview_url', 'render_url']);

    assertIsValidUrl(sourceData.thumbnail_url as string, true);
    assertIsValidUrl(sourceData.preview_url as string, true);
    assertIsValidUrl(sourceData.render_url as string, true);
}

/**
 * Validates media source data
 */
export function assertIsValidMediaSourceData(sourceData: MediaSourceData) {
    assertIsValidBaseSourceData(sourceData);
    assertHasOwnProperties(sourceData, ['type']);
    assertInArray(sourceData.type, Config.ALL_BACKGROUND_TYPES);
    assertNumber(sourceData.width, true);
    assertNumber(sourceData.height, true);
    // verify static base asset properties
    assertHasOwnProperties(sourceData, ['source', 'source_id', 'license']);
    assertInArray(sourceData.license, Config.LICENSES);
    assertString(sourceData.source_id, true);

    // verify static image and video common properties
    assertHasOwnProperties(sourceData, ['is_editorial', 'width', 'height']);
    assertBoolean(sourceData.is_editorial, true);

    // verify static video specific properties
    if (sourceData.type === Config.VIDEO_TYPE_BACKGROUND) {
        assertHasOwnProperties(sourceData, ['poster_url', 'duration', 'has_audio']);
        assertIsValidUrl(sourceData.poster_url as string, true);
        assertNumber(sourceData.duration as number);
        assertBoolean(sourceData.has_audio as boolean, true);
    }
}

/**
 * Validates icon source data
 */
export function assertIsValidIconSourceData(sourceData: IconSourceData) {
    assertObject(sourceData);
    assertHasOwnProperties(sourceData, [
        'url',
        'uuid',
        'animated',
        'source',
        'license',
        'source_id',
    ]);
    assertIsValidUrl(sourceData.url);
    assertString(sourceData.uuid);
    assertBoolean(sourceData.animated as boolean, true);
    assertNumber(sourceData.source);
    assertInArray(sourceData.license, ['', ...Config.LICENSES]);
    assertString(sourceData.source_id as string, true);
}

/**
 * Validates watermark source data
 */
export function assertTemplateWatermarkSource(watermarkSource: TemplateWatermarkSource) {
    assertObject(watermarkSource);
    assertHasOwnProperties(watermarkSource, [TemplateWatermarkProperties.SOURCE_DATA]);
    assertIsValidBaseSourceData(watermarkSource.sourceData);
}

/** Validates transcriptData */
export function assertIsValidTranscriptData(data: TranscriptData) {
    assertHasOwnProperties(data, ['words', 'language', 'textDirection', 'separateWordsWithSpaces']);
    assertInArray(data.textDirection, Config.TEXT_DIRECTIONS);
    assertBoolean(data.separateWordsWithSpaces, false);
    assertArray(data.words, false);
}

/**
 * Test if an index is within the given bounds/range.
 */
export function assertIsValidIndex(index: number, arrayLength: number) {
    assertNumber(index);
    assertNumberBetween(arrayLength, 0, Infinity);

    if (index < 0 || index >= arrayLength) {
        ErrorReporter.setContext('assertIsValidIndex', { index, arrayLength });
        throw new ValidationError('index is out of bounds');
    }
}

/**
 * Check if a 200 response was returned
 */
export function assertResponseOk(response: Response) {
    const status = response.status;
    if (status < 200 || status >= 300) {
        ErrorReporter.setContext('assertResponseOk', { response });
        throw new ValidationError('Response returned a bad status code');
    }
}

/**
 * Check that the name is a valid name of a `BaseAsset` derived class.
 * It has a specific format: (root name)_(count)?_(suffix)?
 */
export function assertAssetName(name: string) {
    // Raises an exception if the name cannot be parsed into components.
    assetNameComponentsFromName(name);
}

/**
 * Provides a compile-time check that a value is never passed.
 * This can be used in case switches and if statement blocks
 * to make sure all cases of a specific type are handled
 */
export function assertNever(value: never): never {
    throw new Error(`Unexpected value: ${value}`);
}
